import { useTheme } from "@emotion/react";
import { BadgeOutlined, Clear, ExpandMore, Search } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../../../../services/api";
import DatagridComponent from "../../../../../../components/datagridComponent";
import useStyles from "./styles";

const OPCOES_PADRAO = [
  {
    priorizeNomeFantasia: false,
    exemplo: "Comércio de Alimentos Bom Sabor Ltda",
  },
  {
    priorizeNomeFantasia: true,
    exemplo: "Empório Bom Sabor",
  },
];

const OPCOES_ORIGEM = [
  { valor: "todos", label: "Todos" },
  { valor: "individual", label: "Individual" },
  { valor: "global", label: "Padrão global" },
];

const ALTURA_LINHA = 64;
const ESPERA_BUSCA_MS = 400;
const LINHAS_POR_PAGINA_PADRAO = 25;

const labelNome = (priorizeNomeFantasia) =>
  priorizeNomeFantasia ? "Nome Fantasia" : "Razão Social";

const temAlteracao = (alteracoes, clienteId) =>
  Object.prototype.hasOwnProperty.call(alteracoes, clienteId);

const mensagemErro = (error, padrao) => error?.response?.data?.error || padrao;

const requisicaoCancelada = (error) =>
  error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

export default function ConfigExibicaoCliente() {
  const theme = useTheme();
  const styles = useStyles(theme);

  // Valor persistido em ConfigClienteGlobal.priorizeNomeFantasia
  const [configGlobal, setConfigGlobal] = useState({
    id: null,
    priorizeNomeFantasia: false,
  });
  // Valor em edição, antes de salvar
  const [priorizeNomeFantasia, setPriorizeNomeFantasia] = useState(false);

  // Página atual de clientes; priorizeNomeFantasia null = segue o padrão global
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalClientes, setTotalClientes] = useState(0);
  const [totalPersonalizadosSalvos, setTotalPersonalizadosSalvos] = useState(0);
  const [paginacao, setPaginacao] = useState({
    page: 0,
    pageSize: LINHAS_POR_PAGINA_PADRAO,
  });
  const [buscarCliente, setBuscarCliente] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [origem, setOrigem] = useState("todos");
  const [loadingTabela, setLoadingTabela] = useState(false);

  // Alterações pendentes, mantidas entre páginas: { [clienteId]: { valor, original } }
  const [alteracoesClientes, setAlteracoesClientes] = useState({});

  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const controllerRef = useRef(null);

  const bloqueado = !editando || salvando;
  const padraoAlterado =
    priorizeNomeFantasia !== configGlobal?.priorizeNomeFantasia;
  const totalClientesAlterados = Object.keys(alteracoesClientes).length;
  const possuiAlteracoes = padraoAlterado || totalClientesAlterados > 0;

  const totalPersonalizados =
    totalPersonalizadosSalvos +
    Object.values(alteracoesClientes).reduce(
      (soma, { valor, original }) =>
        soma + Number(valor !== null) - Number(original !== null),
      0,
    );

  const valorCliente = (cliente) =>
    temAlteracao(alteracoesClientes, cliente.id)
      ? alteracoesClientes[cliente.id].valor
      : cliente.priorizeNomeFantasia;

  const alterarCliente = (cliente, valor) => {
    setAlteracoesClientes((prev) => {
      const proximo = { ...prev };
      if (valor === cliente.priorizeNomeFantasia) {
        delete proximo[cliente.id];
      } else {
        proximo[cliente.id] = {
          valor,
          original: cliente.priorizeNomeFantasia,
        };
      }
      return proximo;
    });
  };

  const columnsCliente = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "clienteErpId",
      headerName: "Código",
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={styles.codigoCliente}>
          {params.row.clienteErpId || "-"}
        </Typography>
      ),
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 1,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => {
        const valor = valorCliente(params.row);
        const nomeFantasiaAtivo = valor === null ? priorizeNomeFantasia : valor;

        return (
          <Box sx={styles.boxNomes}>
            <Typography sx={styles.nomeCliente(!nomeFantasiaAtivo)}>
              <Box component="span" sx={styles.nomeTag(!nomeFantasiaAtivo)}>
                RS
              </Box>
              {params.row.razaoSocial || "-"}
            </Typography>
            <Typography sx={styles.nomeCliente(nomeFantasiaAtivo)}>
              <Box component="span" sx={styles.nomeTag(nomeFantasiaAtivo)}>
                NF
              </Box>
              {params.row.nomeFantasia || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "priorizeNomeFantasia",
      headerName: "Exibir como",
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const valor = valorCliente(params.row);
        const valorSelect =
          valor === null ? "global" : valor ? "fantasia" : "razao";

        return (
          <Select
            size="small"
            fullWidth
            value={valorSelect}
            disabled={bloqueado}
            sx={styles.select}
            onChange={(e) => {
              const selecionado = e.target.value;
              alterarCliente(
                params.row,
                selecionado === "global" ? null : selecionado === "fantasia",
              );
            }}
          >
            <MenuItem value="global" sx={styles.menuItem}>
              Padrão global ({labelNome(priorizeNomeFantasia)})
            </MenuItem>
            <MenuItem value="razao" sx={styles.menuItem}>
              Razão Social
            </MenuItem>
            <MenuItem value="fantasia" sx={styles.menuItem}>
              Nome Fantasia
            </MenuItem>
          </Select>
        );
      },
    },
    {
      field: "origem",
      headerName: "Origem",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={styles.boxOrigem}>
          {valorCliente(params.row) === null ? (
            <Chip
              size="small"
              variant="outlined"
              label="Padrão global"
              sx={styles.chip}
            />
          ) : (
            <Chip
              size="small"
              color="primary"
              label="Individual"
              sx={styles.chip}
            />
          )}
          {temAlteracao(alteracoesClientes, params.row.id) && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Não salvo"
              sx={styles.chip}
            />
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    getConfigGlobal();
  }, []);

  useEffect(() => {
    const temporizador = setTimeout(() => {
      const novaBusca = buscarCliente.trim();
      setBuscaAplicada((atual) => {
        if (novaBusca !== atual) {
          setPaginacao((prev) => ({ ...prev, page: 0 }));
        }
        return novaBusca;
      });
    }, ESPERA_BUSCA_MS);

    return () => clearTimeout(temporizador);
  }, [buscarCliente]);

  const getConfigGlobal = async () => {
    setLoading(true);
    try {
      const response = await api.get("/interno/find/configNomeCliente/global");

      const global = {
        id: response?.data?.id || null,
        priorizeNomeFantasia: response?.data?.priorizeNomeFantasia ?? false,
      };
      setConfigGlobal(global);
      setPriorizeNomeFantasia(global.priorizeNomeFantasia);
    } catch (error) {
      console.error("Erro ao buscar padrão de exibição:", error);
      setSnackbar({
        children: mensagemErro(error, "Erro ao buscar padrão de exibição!"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const buscarPagina = useCallback(async () => {
    // Cancela a busca anterior: digitar rápido dispara várias e só a última interessa
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoadingTabela(true);

    try {
      const response = await api.post(
        "/interno/find/configNomeCliente/clientes",
        {
          busca: buscaAplicada || undefined,
          origem: origem !== "todos" ? origem : undefined,
          pagina: paginacao.page,
          tamanho: paginacao.pageSize,
        },
        { signal: controller.signal },
      );

      setRows(response?.data?.clientes || []);
      setTotal(response?.data?.total || 0);
      setTotalClientes(response?.data?.totalClientes || 0);
      setTotalPersonalizadosSalvos(response?.data?.totalPersonalizados || 0);
    } catch (error) {
      if (requisicaoCancelada(error)) return;
      console.error("Erro ao buscar clientes:", error);
      setSnackbar({
        children: mensagemErro(error, "Erro ao buscar clientes!"),
        severity: "error",
      });
    } finally {
      if (controllerRef.current === controller) setLoadingTabela(false);
    }
  }, [buscaAplicada, origem, paginacao]);

  useEffect(() => {
    buscarPagina();
  }, [buscarPagina]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const updatePadraoGlobal = async () => {
    const response = await api.post(
      "/interno/update/configNomeCliente/global",
      {
        priorizeNomeFantasia: priorizeNomeFantasia,
      },
    );

    const valorSalvo =
      response?.data?.priorizeNomeFantasia ?? priorizeNomeFantasia;
    setConfigGlobal((prev) => ({
      ...prev,
      id: response?.data?.id || prev.id,
      priorizeNomeFantasia: valorSalvo,
    }));
    setPriorizeNomeFantasia(valorSalvo);
  };

  // null é enviado explicitamente para o cliente voltar ao padrão global
  const updateConfigClientes = async () => {
    const clientes = Object.entries(alteracoesClientes).map(
      ([clienteId, { valor }]) => ({
        clienteId,
        priorizeNomeFantasia: valor,
      }),
    );

    await api.post("/interno/update/configNomeCliente/clientes", {
      clientes: clientes,
    });
    setAlteracoesClientes({});
  };

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      if (padraoAlterado) await updatePadraoGlobal();
      if (totalClientesAlterados > 0) await updateConfigClientes();

      setEditando(false);
      setSnackbar({
        children: "Configuração de exibição atualizada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao atualizar configuração de exibição:", error);
      setSnackbar({
        children: mensagemErro(
          error,
          "Erro ao atualizar configuração de exibição!",
        ),
        severity: "error",
      });
    } finally {
      setSalvando(false);
      buscarPagina();
    }
  };

  const handleCancelar = () => {
    setPriorizeNomeFantasia(configGlobal?.priorizeNomeFantasia);
    setAlteracoesClientes({});
    setEditando(false);
  };

  const resumoAlteracoes = () => {
    const partes = [];
    if (padraoAlterado) {
      partes.push(
        <span key="padrao">
          padrão global para <b>{labelNome(priorizeNomeFantasia)}</b>
        </span>,
      );
    }
    if (totalClientesAlterados > 0) {
      partes.push(
        <span key="clientes">
          <b>{totalClientesAlterados}</b>{" "}
          {totalClientesAlterados === 1
            ? "cliente alterado"
            : "clientes alterados"}
        </span>,
      );
    }
    if (partes.length === 0) return "Nenhuma alteração pendente.";

    return (
      <>
        Alterações pendentes: {partes[0]}
        {partes[1] && <> e {partes[1]}</>}.
      </>
    );
  };

  return (
    <>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="exibicao-cliente-content"
          id="exibicao-cliente-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BadgeOutlined color="primary" />
              <Typography variant="h5" sx={styles.title}>
                Configuração de exibição do nome do cliente
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* PADRÃO GLOBAL */}
                <Paper variant="outlined" sx={styles.paperContainer}>
                  <Box sx={styles.boxHeaderBlock}>
                    <Typography variant="body2" sx={styles.titleSwitch}>
                      Padrão de exibição
                    </Typography>
                    <Typography sx={styles.subtitle}>
                      Aplicado a todos os clientes marcados como 'Padrão global'
                      na lista abaixo.
                    </Typography>
                  </Box>

                  <Box
                    sx={styles.optionGroup}
                    role="radiogroup"
                    aria-label="Padrão de exibição"
                  >
                    {OPCOES_PADRAO.map((opcao) => {
                      const selected =
                        priorizeNomeFantasia === opcao.priorizeNomeFantasia;
                      const label = `Padrão ${labelNome(opcao.priorizeNomeFantasia)}`;

                      return (
                        <Paper
                          key={label}
                          variant="outlined"
                          sx={styles.optionCard(selected, bloqueado)}
                          onClick={() => {
                            if (!bloqueado)
                              setPriorizeNomeFantasia(
                                opcao.priorizeNomeFantasia,
                              );
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Radio
                              size="small"
                              checked={selected}
                              disabled={bloqueado}
                              sx={{ p: 0.5 }}
                              inputProps={{ "aria-label": label }}
                              onChange={() =>
                                setPriorizeNomeFantasia(
                                  opcao.priorizeNomeFantasia,
                                )
                              }
                            />
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                            >
                              {label}
                            </Typography>
                            {configGlobal?.priorizeNomeFantasia ===
                              opcao.priorizeNomeFantasia && (
                              <Chip
                                label="Em uso"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ...styles.chipEmUso, ml: "auto" }}
                              />
                            )}
                          </Box>
                          <Paper variant="outlined" sx={styles.paperInfo}>
                            <Typography sx={styles.textInfo}>
                              Ex.: <b>{opcao.exemplo}</b>
                            </Typography>
                          </Paper>
                        </Paper>
                      );
                    })}
                  </Box>
                </Paper>

                {/* CONFIGURAÇÃO POR CLIENTE */}
                <Paper variant="outlined" sx={styles.paperContainer}>
                  <Box sx={styles.boxContainer}>
                    <Box sx={styles.boxHeaderBlock}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2" sx={styles.titleSwitch}>
                          Configuração por cliente
                        </Typography>
                        <Chip
                          size="small"
                          label={`${totalPersonalizados} de ${totalClientes} personalizados`}
                          sx={{ ...styles.chip, height: 20 }}
                        />
                      </Box>
                      <Typography sx={styles.subtitle}>
                        Clientes em 'Padrão global' seguem o padrão acima.
                      </Typography>
                    </Box>
                    <Box sx={styles.boxFiltros}>
                      <TextField
                        select
                        label="Origem"
                        size="small"
                        value={origem}
                        onChange={(event) => {
                          setOrigem(event.target.value);
                          setPaginacao((prev) => ({ ...prev, page: 0 }));
                        }}
                        sx={styles.origemField}
                      >
                        {OPCOES_ORIGEM.map((opcao) => (
                          <MenuItem
                            key={opcao.valor}
                            value={opcao.valor}
                            sx={styles.menuItem}
                          >
                            {opcao.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Buscar cliente"
                        variant="outlined"
                        autoComplete="off"
                        placeholder="Código ou nome"
                        value={buscarCliente}
                        size="small"
                        onChange={(event) => {
                          setBuscarCliente(event?.target?.value?.trimStart());
                        }}
                        sx={styles.searchField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                          endAdornment: buscarCliente ? (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setBuscarCliente("")}
                                edge="end"
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Box>
                  </Box>

                  <Paper
                    variant="outlined"
                    elevation={3}
                    sx={styles.paperDatagrid}
                  >
                    <DatagridComponent
                      columns={columnsCliente}
                      rows={rows}
                      loading={loadingTabela}
                      isPagination
                      sxDatagrid={styles.datagridConfortavel(ALTURA_LINHA)}
                      options={{
                        getRowHeight: () => ALTURA_LINHA,
                        paginationMode: "server",
                        rowCount: total,
                        paginationModel: paginacao,
                        onPaginationModelChange: setPaginacao,
                      }}
                    />
                  </Paper>
                </Paper>
              </Box>
            )}
          </Paper>

          <Box sx={styles.containerActions}>
            {editando ? (
              <>
                <Typography sx={styles.textPendente}>
                  {resumoAlteracoes()}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={salvando}
                  onClick={handleCancelar}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={salvando || !possuiAlteracoes}
                  onClick={handleSalvar}
                  startIcon={
                    salvando ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                >
                  Salvar
                </Button>
              </>
            ) : (
              <>
                <Typography sx={styles.textPendente}>
                  Clique em Editar para alterar o padrão e a configuração dos
                  clientes.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={loading}
                  onClick={() => setEditando(true)}
                >
                  Editar
                </Button>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
